import { TALK_SESSION_LISTING_TYPE } from './sessionPricing';

/**
 * Sweet Listeners sessions are remote chat/voice/video.
 * Listing location (map, street address, neighborhood) is not collected or shown.
 */
/**
 * Listing-field keys the Details wizard saves itself (or Pricing saves).
 * Console may mark these required; they must not block leaving Details.
 */
export const WIZARD_MANAGED_LISTING_FIELD_KEYS = [
  'offeredFormats',
  'topics',
  'topicsOther',
  'listeningStyles',
  'profileAge',
  'profileBirthdate',
  'profileSex',
  'profileSexOther',
  'profileCity',
  'showAge',
  'showSex',
  'showCity',
  'showAgeRange',
  'profileAgeRange',
];

export const LOCATION_LIKE_FIELD_KEYS = [
  'location',
  'address',
  'neighborhood',
  'geolocation',
  'origin',
  'building',
  'city',
];

const LOCATION_KEY_RE = /^(location|address|neighborhood|geolocation|origin|building|city)$/i;

export const isTalkSessionType = listingType => listingType === TALK_SESSION_LISTING_TYPE;

export const listingTypeHidesLocation = listingType => isTalkSessionType(listingType);

export const listingHidesLocation = listing =>
  listingTypeHidesLocation(listing?.attributes?.publicData?.listingType);

export const isLocationLikeListingField = fieldConfig => {
  const key = fieldConfig?.key || '';
  return LOCATION_KEY_RE.test(key);
};

/**
 * True when a Console listing field should not decide if Details is complete.
 * Talk sessions hide location; formats/topics/styles/reveals are saved elsewhere.
 */
export const skipListingFieldForDetailsCompletion = fieldConfig => {
  const key = fieldConfig?.key || '';
  return (
    WIZARD_MANAGED_LISTING_FIELD_KEYS.includes(key) || isLocationLikeListingField(fieldConfig)
  );
};

export const dropLocationLikeListingFields = listingFields =>
  (listingFields || []).filter(field => {
    if (!isLocationLikeListingField(field)) {
      return true;
    }
    const limited = field.listingTypeConfig?.limitToListingTypeIds;
    const ids = field.listingTypeConfig?.listingTypeIds || [];
    if (!limited) {
      return false;
    }
    return !ids.includes(TALK_SESSION_LISTING_TYPE);
  });

export const disableLocationForTalkSessions = listingTypes =>
  (listingTypes || []).map(lt => {
    if (!isTalkSessionType(lt.listingType)) {
      return lt;
    }
    return {
      ...lt,
      defaultListingFields: {
        ...(lt.defaultListingFields || {}),
        location: false,
      },
    };
  });

export const isRemoteOnlyMarketplace = listingTypes => {
  const types = listingTypes || [];
  return types.length > 0 && types.every(lt => lt.defaultListingFields?.location === false);
};

/**
 * Never persist street addresses or coordinates on talksession writes.
 * Sharetribe accepts null to clear previously saved location data.
 */
export const omitStoredListingLocation = (payload, listingType) => {
  const type = listingType || payload?.publicData?.listingType;
  if (!isTalkSessionType(type)) {
    return payload;
  }

  const next = { ...payload };
  const { location, neighborhood, address, building, ...restPublic } = next.publicData || {};
  // create_draft accepts an optional LatLng object — JSON null is not a
  // location and can 400. Only send null on update to clear a saved pin.
  if (next.id) {
    next.geolocation = null;
    next.publicData = next.publicData
      ? {
          ...restPublic,
          location: null,
        }
      : next.publicData;
  } else {
    delete next.geolocation;
    if (next.publicData) {
      next.publicData = { ...restPublic };
    }
  }
  return next;
};
